import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAudio } from '@utils/providers/audio-context';
import { formatDuration } from '@utils/date';

/**
 * Screen-reader textual alternative for the peaks.js waveform canvas (#1920).
 *
 * The canvas itself is unreachable for AT users — they can't see the wave or
 * the playhead. This live region narrates the bits that matter:
 *   - duration once the audio finishes loading (so SR users know how long
 *     the recording is before pressing Play)
 *   - play / pause transitions, with the current timestamp at the moment of
 *     the transition so the user knows where they paused
 *
 * We deliberately do NOT announce on every timeupdate — that would be
 * 4–8 events per second and unusable. The current-time-on-demand surface is
 * the visible time-code in the toolbar, which a11y-1920 already labeled.
 *
 * Region is sr-only so it doesn't render visually. Polite so it doesn't
 * interrupt the user — they're probably typing while the audio plays.
 */
export function WaveformLiveRegion() {
  const { t } = useTranslation();
  const { isPlaying, currentTime, duration } = useAudio();
  const [announcement, setAnnouncement] = useState('');

  // Track previous values so we only announce on transitions, not on every
  // re-render. Refs (not state) so the comparison is fresh inside the effect
  // body without becoming a deps loop.
  const prevPlayingRef = useRef<boolean | null>(null);
  const prevDurationRef = useRef<number>(0);

  // Announce duration once, the first time it becomes known.
  useEffect(() => {
    if (duration > 0 && prevDurationRef.current === 0) {
      setAnnouncement(`${t('audio-duration')}: ${formatDuration(duration)}`);
      prevDurationRef.current = duration;
    }
  }, [duration, t]);

  // Announce play / pause transitions, plus the timestamp at the moment of
  // the transition (matters most when pausing — SR user wants to know where
  // they stopped).
  useEffect(() => {
    if (prevPlayingRef.current === null) {
      prevPlayingRef.current = isPlaying;
      return;
    }
    if (prevPlayingRef.current !== isPlaying) {
      const at = formatDuration(currentTime) || '0s';
      setAnnouncement(
        isPlaying ? `${t('playing')}: ${at}.` : `${t('paused')}: ${at}.`
      );
      prevPlayingRef.current = isPlaying;
    }
    // currentTime intentionally NOT in the deps list — we want the snapshot
    // at the moment isPlaying flips, not a re-render every tick.
  }, [isPlaying, t]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {announcement}
    </div>
  );
}
